import { OpenFeature, type Client, type EvaluationContext } from "@openfeature/web-sdk";
import { PostHogWebProvider } from "./posthog-provider";
import type { FeatureFlags, FlagsConfig, FlagsContext, Unsubscribe } from "./types";

/**
 * Default `FeatureFlags` adapter built on the OpenFeature web SDK backed by the
 * PostHog provider. This is the bridge between the vendor-neutral interface and
 * OpenFeature; callers only ever see {@link FeatureFlags}.
 */
export class OpenFeatureFlags implements FeatureFlags {
  private readonly provider = new PostHogWebProvider();
  private client: Client | null = null;
  private ready = false;
  private initializing: Promise<void> | null = null;

  async init(config?: FlagsConfig): Promise<void> {
    if (this.ready) return;
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      if (config?.context) {
        await OpenFeature.setContext(toEvaluationContext(config.context));
      }
      await OpenFeature.setProviderAndWait(this.provider);
      this.client = OpenFeature.getClient();
      this.ready = true;
    })();

    return this.initializing;
  }

  async setContext(context: FlagsContext): Promise<void> {
    await OpenFeature.setContext(toEvaluationContext(context));
  }

  getBoolean(key: string, defaultValue: boolean): boolean {
    if (!this.client) return defaultValue;
    return this.client.getBooleanValue(key, defaultValue);
  }

  getString(key: string, defaultValue: string): string {
    if (!this.client) return defaultValue;
    return this.client.getStringValue(key, defaultValue);
  }

  getNumber(key: string, defaultValue: number): number {
    if (!this.client) return defaultValue;
    return this.client.getNumberValue(key, defaultValue);
  }

  onChange(listener: () => void): Unsubscribe {
    // Subscribe directly to the PostHog provider's flag-reload signal so hooks
    // re-render on configuration changes regardless of init ordering.
    return this.provider.subscribe(listener);
  }
}

function toEvaluationContext(context: FlagsContext): EvaluationContext {
  // OpenFeature's EvaluationContext index signature forbids `undefined` values (and
  // targetingKey must be a string when present), so drop every undefined entry.
  return Object.fromEntries(
    Object.entries(context).filter(([, value]) => value !== undefined),
  ) as EvaluationContext;
}

/** Shared, single-instance flags client for the workspace. */
export const flags: FeatureFlags = new OpenFeatureFlags();
